import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * Tells the click that ends a called-off drag from a real one. A tool whose parts drag out of the
 * toolbar and whose button acts on a click must not act when the dragged part is let go over the
 * toolbar: the browser still reports that release as a click on the button.
 */
export class DragClickGuard implements ReactiveController {
  private _calledOff = false;

  private readonly _reset = () => {
    this._calledOff = false;
  };

  constructor(private readonly host: ReactiveControllerHost & HTMLElement) {
    host.addController(this);
  }

  /** For the drag controller's `onCanceled`. */
  readonly calledOff = () => {
    this._calledOff = true;
  };

  /** Whether the click handled now ends a called-off drag. */
  endsCalledOffDrag() {
    const calledOff = this._calledOff;
    this._calledOff = false;
    return calledOff;
  }

  // A new press or key starts a new gesture; a drag called off earlier no longer concerns its click.
  hostConnected() {
    this.host.addEventListener('pointerdown', this._reset, true);
    this.host.addEventListener('keydown', this._reset, true);
  }

  hostDisconnected() {
    this.host.removeEventListener('pointerdown', this._reset, true);
    this.host.removeEventListener('keydown', this._reset, true);
  }
}
